import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { UserService } from '../../../../services/user.service';
import { StudentService, Student } from '../../../../services/student.service';
import { ProfileService } from '../../../../services/profile.service';
import { Profile } from '../../../../models/profile';
import { Interest } from '../../../../models/interest';
import { InterestService } from '../../../../services/interests.service';

@Component({
  selector: 'app-profile-strip',
  imports: [RouterLink, CommonModule, MatProgressBarModule, MatButtonModule],
  templateUrl: './profile-strip.html',
  styleUrl: './profile-strip.css',
})
export class ProfileStrip implements OnInit {
  /** Service for accessing and updating the current authenticated user. */
  private userService = inject(UserService);
  /** Service for fetching the student record by user ID. */
  private studentService = inject(StudentService);
  /** Service for fetching and updating the profile record. */
  private profileService = inject(ProfileService);
  /** Used to manually trigger change detection after async updates. */
  private cdr = inject(ChangeDetectorRef);
  /** Used to manually trigger change detection after async updates. */
  private interestService = inject(InterestService);

  /** The current student record, loaded on init. */
  student: Student | null = null;

  /** The current student's profile record, loaded on init. */
  profile: Profile | null = null;

  /** The current student's interests, loaded on init. */
  interests: Interest[] = [];

  constructor() {}

  /**
   * Loads student data on init and re-loads whenever the user record is updated
   * (e.g. after a profile edit).
   * @returns {void}
   */
  ngOnInit(): void {
    this.loadStudentData();
    this.userService.userUpdated$.subscribe(() => {
      this.loadStudentData();
    });
  }

  /**
   * Returns the current authenticated user from UserService.
   * @returns {User | null}
   */
  get currentUser() {
    return this.userService.currentUser;
  }

  /**
   * Returns the current user's uppercase initials, falling back to '?'.
   * @returns {string}
   */
  get initials(): string {
    const user = this.currentUser;
    if (!user) return '?';
    const f = (user.preferredName || user.firstName)?.[0] ?? '';
    const l = user.lastName?.[0] ?? '';
    return (f + l).toUpperCase() || '?';
  }

  /**
   * Returns the current user's full name as a trimmed string.
   * @returns {string}
   */
  get fullName(): string {
    const user = this.currentUser;
    if (!user) return '';
    return `${(user.preferredName || user.firstName) ?? ''} ${user.lastName ?? ''}`.trim();
  }

  /**
   * Maps the student's numeric grade level to a human-readable label.
   * @returns {string}
   */
  get gradeLabel(): string {
    const grade = this.student?.grade_level;
    if (!grade) return '';
    const labels: Record<number, string> = {
      6: '6th Grade',
      7: '7th Grade',
      8: '8th Grade',
      9: 'Freshman',
      10: 'Sophomore',
      11: 'Junior',
      12: 'Senior',
    };
    return labels[grade] ?? `Grade ${grade}`;
  }

  /**
   * Calculates profile completion percentage based on which key fields are filled.
   * Note: this is computed client-side and may differ slightly from the persisted
   * `profile_complete_percent` until `syncProfilePercent` runs.
   * @returns {number} Completion percentage (0–100).
   */
  get profilePercent(): number {
    const user = this.currentUser;
    const fields = [
      !!(user?.firstName && user?.lastName),
      !!this.student?.grade_level,
      !!this.interests?.length,
      !!this.profile?.career_id,
      !!this.profile?.bio,
      !!this.profile?.profile_picture_path,
    ];
    const filled = fields.filter(Boolean).length;
    return Math.round((filled / fields.length) * 100);
  }

  /**
   * Ensures the current user is loaded before fetching the student record.
   * If the user isn't cached yet, fetches it first.
   * @returns {void}
   */
  loadStudentData(): void {
    const user = this.userService.currentUser;
    if (!user) {
      this.userService.getCurrentUser().subscribe({
        next: () => this.fetchStudent(),
        error: (err) => console.error(err),
      });
    } else {
      this.fetchStudent();
      this.loadInterests();
    }
  }

  /**
   * Fetches the student and profile records, then syncs the completion percentage to the backend.
   * @returns {void}
   */
  fetchStudent(): void {
    const user = this.userService.currentUser;
    if (!user) return;

    this.studentService.getStudentByUserId(user.id).subscribe({
      next: (student) => {
        this.student = student;
        this.cdr.detectChanges();
        this.profileService.getMyProfile().subscribe({
          next: (res) => {
            if (res.exists && res.profile) {
              this.profile = res.profile;
              this.cdr.detectChanges();
              this.syncProfilePercent();
            }
          },
        });
      },
      error: (err) => console.error(err),
    });
  }

  loadInterests() {
    this.interestService.getStudentInterests().subscribe((data) => {
      this.interests = data;
    });
  }

  /** Persists the authenticated user's profile completion percentage. */
  syncProfilePercent(): void {
    const percent = this.profilePercent;
    if (this.profile?.profile_complete_percent === percent) return;
    this.profileService.updateMyProfile({ profile_complete_percent: percent }).subscribe({
      next: (updated) => {
        if (this.profile) this.profile.profile_complete_percent = updated.profile_complete_percent;
      },
      error: (err) => console.error(err),
    });
  }
}
